import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../shared/Crud';
import responsibleReceiptService from '../../services/responsibleReceiptService';
import { maskCPF, maskPhone } from '../../utils/masks';
import PhoneActionMenu from '../shared/PhoneActionMenu';
import { CheckIcon, SearchIcon } from '../Layout/icons';
import MessageModal from '../shared/MessageModal';

const { Button, PaginationContainer, PageInfo, PageButtons, FormGroup, Input, FiltersContainer, FilterGroup } = CrudStyles;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
`;

const ModalBox = styled.div`
    box-sizing: border-box;
    background: white;
    padding: 24px;
    border-radius: 12px;
    width: 100%;
    max-width: min(680px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    overflow: hidden;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-shrink: 0;
    h2 { margin: 0; font-size: 18px; color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'}; }
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #666;
    font-size: 24px;
    line-height: 1;
    &:hover { color: #222; }
`;

const TableWrap = styled.div`
    overflow: auto;
    margin-bottom: 16px;
    flex: 1;
    min-height: 120px;
`;

const ModalTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    th, td {
        padding: 14px 16px;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme?.colors?.gray?.[100] || '#eee'};
        vertical-align: middle;
    }
    th {
        background: ${({ theme }) => theme?.colors?.background || '#f5f5f5'};
        font-weight: 600;
        color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
        user-select: none;
    }
    th.sortable {
        cursor: pointer;
    }
    th.sortable:hover {
        background: ${({ theme }) => theme?.colors?.gray?.[100] || '#eee'};
    }
    .sort-icon { margin-left: 4px; }
`;

const SelectableRow = styled.tr`
    cursor: pointer;
    &:hover { background: ${({ theme }) => (theme?.colors?.primary?.light ? `${theme.colors.primary.light}12` : 'rgba(59, 130, 246, 0.08)')}; }
`;

const ActionBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme?.colors?.primary?.main || '#3b5998'};
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme?.colors?.gray?.[200] || '#e5e7eb'};
    &:hover {
        background: ${({ theme }) => (theme?.colors?.primary?.light ? `${theme.colors.primary.light}25` : 'rgba(59,89,152,0.1)')};
    }
    svg { width: 16px; height: 16px; flex-shrink: 0; }
`;

const CreateSection = styled.div`
    border-top: 1px solid ${({ theme }) => theme?.colors?.gray?.[200] || '#e5e7eb'};
    padding-top: 20px;
    margin-top: 8px;
    flex-shrink: 0;
`;
const CreateTitle = styled.p`
    font-weight: 600;
    margin: 0 0 12px 0;
    font-size: 14px;
    color: ${({ theme }) => theme?.colors?.gray?.[900] || '#374151'};
`;

function formatPhoneDisplay(pn) {
    if (!pn) return '—';
    if (typeof pn === 'string') return pn;
    const area = pn.areaCode ?? '';
    const num = pn.number ?? '';
    if (area && num) return `(${area}) ${num.length === 9 ? num.replace(/(\d{5})(\d{4})/, '$1-$2') : num}`;
    return num || area || '—';
}

export default function ResponsibleReceiptModal({
    customerId,
    onSelect,
    onClose,
}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [pageMeta, setPageMeta] = useState({ totalPages: 0, first: true, last: true });
    const [activeFilter, setActiveFilter] = useState('true');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', document: '', phone: '' });
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    const sort = `${sortField},${sortDirection}`;
    const active = activeFilter === 'true';

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        responsibleReceiptService
            .list({ customerId: customerId ?? undefined, page, size: 10, sort, active })
            .then((res) => {
                if (cancelled) return;
                setData(res.content ?? []);
                setPageMeta(res.page ?? { totalPages: 0, first: true, last: true });
            })
            .catch(() => { if (!cancelled) setData([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [customerId, page, sort, active]);

    const handleSortClick = (field) => {
        if (field !== 'name') return;
        setSortDirection((d) => (sortField === field && d === 'asc' ? 'desc' : 'asc'));
        setSortField(field);
        setPage(0);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return null;
        return <span className="sort-icon">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>;
    };

    const handleSearch = () => {
        setPage(0);
    };

    const handleSelect = (row) => {
        onSelect?.({
            id: row.id,
            name: row.name,
            document: row.document,
            phoneNumber: row.phoneNumber,
            phone: formatPhoneDisplay(row.phoneNumber),
        });
        onClose?.();
    };

    const handleCreate = async () => {
        const name = (createForm.name ?? '').trim();
        if (!name) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'Informe o nome do responsável.',
            });
            return;
        }
        setCreating(true);
        try {
            const created = await responsibleReceiptService.create(
                {
                    name,
                    document: createForm.document || null,
                    phoneNumber: createForm.phone || null,
                },
                customerId
            );
            const id = created?.id;
            if (id != null) {
                onSelect?.({
                    id: Number(id),
                    name: created?.name ?? name,
                    document: (created?.document ?? createForm.document) || null,
                    phoneNumber: (created?.phoneNumber ?? createForm.phone) || null,
                    phone: createForm.phone || null,
                });
            } else {
                onSelect?.({ name, document: createForm.document || null, phoneNumber: createForm.phone || null, phone: createForm.phone || null });
            }
            onClose?.();
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Erro ao criar responsável.',
            });
        } finally {
            setCreating(false);
        }
    };

    return (
        <>
            <Overlay onClick={(e) => e.target === e.currentTarget && onClose?.()}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <h2>Selecionar ou criar responsável pelo recebimento</h2>
                    <CloseBtn type="button" onClick={onClose} title="Fechar">×</CloseBtn>
                </ModalHeader>
                <FiltersContainer>
                    <FilterGroup>
                        <label>Status</label>
                        <select
                            value={activeFilter}
                            onChange={(e) => { setActiveFilter(e.target.value); setPage(0); }}
                        >
                            <option value="true">Ativo</option>
                            <option value="false">Inativo</option>
                        </select>
                    </FilterGroup>
                    <Button type="button" variant="primary" onClick={handleSearch} style={{ height: '38px' }}>
                        <SearchIcon />
                        Pesquisar
                    </Button>
                </FiltersContainer>
                <TableWrap>
                    <ModalTable>
                        <thead>
                            <tr>
                                <th
                                    className="sortable"
                                    onClick={() => handleSortClick('name')}
                                >
                                    Nome
                                    {getSortIcon('name')}
                                </th>
                                <th>Documento</th>
                                <th>Telefone</th>
                                <th style={{ width: '120px' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px' }}>Carregando...</td></tr>
                            ) : data.length === 0 ? (
                                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>Nenhum responsável encontrado.</td></tr>
                            ) : (
                                data.map((row) => (
                                    <SelectableRow key={row.id} onClick={() => handleSelect(row)}>
                                        <td>{row.name ?? '—'}</td>
                                        <td>{row.document ?? '—'}</td>
                                        <td>
                                            <PhoneActionMenu phoneNumber={formatPhoneDisplay(row.phoneNumber)} />
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <ActionBtn type="button" onClick={() => handleSelect(row)}>
                                                <CheckIcon />
                                                Selecionar
                                            </ActionBtn>
                                        </td>
                                    </SelectableRow>
                                ))
                            )}
                        </tbody>
                    </ModalTable>
                </TableWrap>
                <PaginationContainer>
                    <PageInfo>
                        Página {pageMeta.totalPages === 0 ? 0 : page + 1} de {pageMeta.totalPages || 0}
                    </PageInfo>
                    <PageButtons>
                        <Button variant="outline" disabled={pageMeta.totalPages === 0 || pageMeta.first} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                            Anterior
                        </Button>
                        <Button variant="outline" disabled={pageMeta.totalPages === 0 || pageMeta.last} onClick={() => setPage((p) => p + 1)}>
                            Próxima
                        </Button>
                    </PageButtons>
                </PaginationContainer>
                <CreateSection>
                    <CreateTitle>Criar novo responsável</CreateTitle>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                        <FormGroup style={{ flex: '1 1 160px', minWidth: 0 }}>
                            <label>Nome</label>
                            <Input
                                value={createForm.name}
                                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="Nome completo"
                            />
                        </FormGroup>
                        <FormGroup style={{ flex: '0 0 140px' }}>
                            <label>CPF</label>
                            <Input
                                value={createForm.document}
                                onChange={(e) => setCreateForm((f) => ({ ...f, document: maskCPF(e.target.value) }))}
                                placeholder="000.000.000-00"
                                maxLength={14}
                            />
                        </FormGroup>
                        <FormGroup style={{ flex: '0 0 150px' }}>
                            <label>Telefone</label>
                            <Input
                                value={createForm.phone}
                                onChange={(e) => setCreateForm((f) => ({ ...f, phone: maskPhone(e.target.value) }))}
                                placeholder="(00) 00000-0000"
                                maxLength={15}
                            />
                        </FormGroup>
                        <Button type="button" variant="primary" onClick={handleCreate} disabled={creating || !createForm.name?.trim()}>
                            {creating ? 'Criando...' : 'Criar e selecionar'}
                        </Button>
                    </div>
                </CreateSection>
            </ModalBox>
        </Overlay>
            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
        </>
    );
}
